import type { CampaignAudienceImportStatusName } from "@multi-chat/shared";

export type AudienceSourceApplyKind = "none" | "add" | "update" | "remove-all";

export type AudienceSourceApplyCta = {
  kind: AudienceSourceApplyKind;
  show: boolean;
  disabled: boolean;
  count: number;
};

export function areAudienceIdListsEqual(
  left: readonly number[],
  right: readonly number[],
): boolean {
  if (left.length !== right.length) return false;
  const a = left.toSorted((x, y) => x - y);
  const b = right.toSorted((x, y) => x - y);
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function isAudienceIdSourceDirty(
  committed: readonly number[],
  draft: readonly number[],
): boolean {
  return !areAudienceIdListsEqual(committed, draft);
}

export function audienceIdSourceApplyKind(
  committed: readonly number[],
  draft: readonly number[],
): AudienceSourceApplyKind {
  if (!isAudienceIdSourceDirty(committed, draft)) return "none";
  if (committed.length > 0 && draft.length === 0) return "remove-all";
  if (committed.length === 0) return "add";
  return "update";
}

export function idSourceApplyCta(
  committed: readonly number[],
  draft: readonly number[],
): AudienceSourceApplyCta {
  const kind = audienceIdSourceApplyKind(committed, draft);
  const count = draft.length;
  if (kind === "none") {
    return {
      kind,
      show: committed.length === 0,
      disabled: true,
      count,
    };
  }
  return {
    kind,
    show: true,
    disabled: false,
    count,
  };
}

export function isCsvSourceDirty(
  appliedUploadId: string | null,
  workingUploadId: string | null,
): boolean {
  return (appliedUploadId ?? null) !== (workingUploadId ?? null);
}

export function csvSourceApplyCta(input: {
  appliedUploadId: string | null;
  workingUploadId: string | null;
  workingStatus: CampaignAudienceImportStatusName | null;
  validCount: number;
}): AudienceSourceApplyCta {
  const dirty = isCsvSourceDirty(input.appliedUploadId, input.workingUploadId);
  const ready =
    input.workingStatus === "READY" && input.workingUploadId != null && input.validCount > 0;
  if (!dirty || !ready) {
    return {
      kind: "none",
      show: false,
      disabled: true,
      count: input.validCount,
    };
  }
  const kind: AudienceSourceApplyKind = input.appliedUploadId ? "update" : "add";
  return {
    kind,
    show: true,
    disabled: false,
    count: input.validCount,
  };
}

export function hasUnappliedAudienceSourceChanges(input: {
  committedContactIds: readonly number[];
  draftContactIds: readonly number[];
  committedGroupIds: readonly number[];
  draftGroupIds: readonly number[];
  appliedCsvUploadId: string | null;
  workingCsvUploadId: string | null;
}): boolean {
  return (
    isAudienceIdSourceDirty(input.committedContactIds, input.draftContactIds) ||
    isAudienceIdSourceDirty(input.committedGroupIds, input.draftGroupIds) ||
    isCsvSourceDirty(input.appliedCsvUploadId, input.workingCsvUploadId)
  );
}

export function sourceApplyI18nKey(
  source: "contacts" | "groups" | "csv",
  kind: AudienceSourceApplyKind,
): string {
  switch (kind) {
    case "remove-all":
      return source === "csv" ? "campaigns.csv.remove" : `campaigns.${source}.removeAll`;
    case "update":
      return source === "csv" ? "campaigns.csv.updateFile" : `campaigns.${source}.update`;
    case "add":
    case "none":
      return source === "csv" ? "campaigns.csv.addToCampaign" : `campaigns.${source}.add`;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}
